// ────────────────────────────────────────────────────────────────────────────
// Slide Studio – Core type definitions
// ────────────────────────────────────────────────────────────────────────────

/** Pixel position on the 1920×1080 canvas */
export interface Position {
  x: number
  y: number
}

/** Pixel size */
export interface Size {
  width: number
  height: number
}

/** Bounding rectangle (position + size) */
export interface Rect extends Position, Size {}

// ─── Element types ──────────────────────────────────────────────────────────

export type ElementType = 'text' | 'image' | 'shape' | 'container' | 'group' | 'markdown'

/**
 * Helper to check if an element is a text-like element (covers both 'text'
 * and legacy 'markdown' type discriminants).
 */
export function isTextElement(el: SlideElement): el is TextElement {
  return el.type === 'text' || el.type === 'markdown'
}

export type ShapeKind = 'rectangle' | 'ellipse' | 'line'

export type ContainerLayout = 'row' | 'column' | 'stack'
export type FlexAlign = 'flex-start' | 'center' | 'flex-end' | 'stretch'
export type FlexJustify = 'flex-start' | 'center' | 'flex-end' | 'space-between' | 'space-around' | 'space-evenly'

export type TextAlign = 'left' | 'center' | 'right'
export type VerticalAlign = 'top' | 'middle' | 'bottom'
export type FontWeight = 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900
export type ObjectFit = 'cover' | 'contain' | 'fill' | 'none'

// ─── Constraints (Figma / Xcode style) ──────────────────────────────────────

/**
 * Horizontal constraint – how the element is pinned/scaled horizontally
 * relative to its parent (the canvas, a group, or a container).
 *
 *  • 'left'        – fixed distance from the left edge
 *  • 'right'       – fixed distance from the right edge
 *  • 'left-right'  – pinned to both edges (stretches with parent)
 *  • 'center'      – centered horizontally, fixed width
 *  • 'scale'       – proportional position & width relative to parent
 */
export type HorizontalConstraint = 'left' | 'right' | 'left-right' | 'center' | 'scale'

/**
 * Vertical constraint – how the element is pinned/scaled vertically.
 *
 *  • 'top'         – fixed distance from the top edge
 *  • 'bottom'      – fixed distance from the bottom edge
 *  • 'top-bottom'  – pinned to both edges (stretches with parent)
 *  • 'center'      – centered vertically, fixed height
 *  • 'scale'       – proportional position & height relative to parent
 */
export type VerticalConstraint = 'top' | 'bottom' | 'top-bottom' | 'center' | 'scale'

/**
 * Constraints configuration for an element.
 * Stores both the constraint mode and the computed inset values so the
 * element can be repositioned when the parent resizes.
 */
export interface Constraints {
  horizontal: HorizontalConstraint
  vertical: VerticalConstraint
}

/**
 * Resolved inset distances from the parent's edges.
 * These are computed when the constraint is first set (or when the user
 * manually repositions the element) and used when the parent resizes.
 */
export interface ConstraintInsets {
  top: number
  right: number
  bottom: number
  left: number
}

// ─── Style sub-types ────────────────────────────────────────────────────────

export interface TextStyle {
  fontFamily: string
  fontSize: number
  fontWeight: FontWeight
  fontStyle: 'normal' | 'italic'
  textDecoration: 'none' | 'underline' | 'line-through'
  lineHeight: number // multiplier, e.g. 1.2
  letterSpacing: number // px
  textTransform: 'none' | 'uppercase' | 'lowercase' | 'capitalize'
  textAlign: TextAlign
  verticalAlign: VerticalAlign
  color: string // hex
}

export interface BorderStyle {
  width: number
  color: string
  radius: number // border-radius in px
}

export interface ShadowStyle {
  x: number
  y: number
  blur: number
  spread: number
  color: string
}

// ─── Element definitions ────────────────────────────────────────────────────

interface BaseElement {
  id: string
  type: ElementType
  name: string
  rect: Rect
  rotation: number // degrees
  opacity: number // 0..1
  locked: boolean
  visible: boolean
  zIndex: number
  /** Constraint anchoring (Figma / Xcode style) – defaults to left + top */
  constraints: Constraints
  /** Cached insets from parent edges when constraints were last applied */
  constraintInsets: ConstraintInsets
  /** ID of the group this element belongs to (null = top-level) */
  groupId: string | null
}

/**
 * TextElement – a rich-text element whose content is authored via a TipTap
 * editor (with optional Markdown source). In view mode the content is
 * rendered to HTML. The `type` discriminant accepts both `'text'` and the
 * legacy `'markdown'` value so that existing saved data continues to work.
 */
export interface TextElement extends BaseElement {
  type: 'text' | 'markdown'
  content: string
  textStyle: TextStyle
  backgroundColor: string // hex or 'transparent'
  padding: { top: number; right: number; bottom: number; left: number }
  border: BorderStyle
}

/**
 * @deprecated Use `TextElement` instead. Kept as a type alias for backward
 * compatibility with code that still references `MarkdownTextElement`.
 */
export type MarkdownTextElement = TextElement

/**
 * Crop region expressed as fractions (0–1) of the source image.
 * E.g. { x: 0.1, y: 0.2, width: 0.6, height: 0.5 } means the visible
 * portion starts at 10% from the left / 20% from the top and spans
 * 60% of the width / 50% of the height of the original image.
 *
 * `null` means no crop — the full image is shown.
 */
export interface CropRegion {
  x: number
  y: number
  width: number
  height: number
}

export interface ImageElement extends BaseElement {
  type: 'image'
  src: string // URL or data-uri
  alt: string
  objectFit: ObjectFit
  border: BorderStyle
  backgroundColor: string
  /** Optional crop region (fractions 0–1 of the source image). null = no crop */
  cropRegion: CropRegion | null
}

export interface ShapeElement extends BaseElement {
  type: 'shape'
  shapeKind: ShapeKind
  fill: string // hex or 'transparent'
  border: BorderStyle
  shadow: ShadowStyle | null
}

export interface ContainerElement extends BaseElement {
  type: 'container'
  layout: ContainerLayout
  /** IDs of child elements that live inside this container */
  childIds: string[]
  /** Gap between children in px (ignored for 'stack' layout) */
  gap: number
  /** Cross-axis alignment for row/column layouts */
  alignItems: FlexAlign
  /** Main-axis distribution for row/column layouts */
  justifyContent: FlexJustify
  /** Whether row/column children wrap */
  wrap: boolean
  /** Inner padding */
  padding: { top: number; right: number; bottom: number; left: number }
  /** Background color */
  backgroundColor: string
  /** Border */
  border: BorderStyle
  /** Whether to clip children that overflow */
  clipContent: boolean
}

/**
 * GroupElement – a lightweight Figma-style group.
 *
 * Unlike a container, a group does NOT impose a layout (no flexbox).
 * Children keep their absolute positions *relative to the group's origin*.
 * The group's rect is always the bounding box of its children.
 *
 * When the group is resized, its children are repositioned / resized
 * according to their individual `constraints`.
 */
export interface GroupElement extends BaseElement {
  type: 'group'
  /** IDs of child elements in this group (order = visual stacking) */
  childIds: string[]
  /** Whether to clip children that overflow the group bounds */
  clipContent: boolean
}

export type SlideElement = TextElement | ImageElement | ShapeElement | ContainerElement | GroupElement

// ─── Slide & Editor state ───────────────────────────────────────────────────

export interface Slide {
  id: string
  name: string
  backgroundColor: string
  width: number
  height: number
  elements: SlideElement[]
}

export type EditorTool = 'select' | 'text' | 'image' | 'shape' | 'container' | 'hand'

/**
 * Marquee / lasso selection box state.
 * Rendered as a dashed rectangle on the canvas while the user drags.
 */
export interface SelectionBox {
  /** Canvas-space start position */
  startX: number
  startY: number
  /** Canvas-space current position (follows the mouse) */
  currentX: number
  currentY: number
}

export interface EditorState {
  /** Presentation-level title used for export/download naming */
  presentationTitle: string
  /** All slides (for now we support a single slide, but the model is ready) */
  slides: Slide[]
  /** Index of the currently-active slide */
  activeSlideIndex: number
  /** ID of the currently selected element, or null */
  selectedElementId: string | null
  /** IDs of multi-selected elements */
  selectedElementIds: string[]
  /** Current active tool */
  activeTool: EditorTool
  /** Canvas zoom level (1 = 100%) */
  zoom: number
  /** Canvas scroll / pan offset */
  panOffset: Position
  /** Shape sub-type to draw when tool === 'shape' */
  activeShapeKind: ShapeKind
  /** Container layout sub-type to draw when tool === 'container' */
  activeContainerLayout: ContainerLayout
  /** Whether the user is currently dragging / resizing */
  isDragging: boolean
  /** Whether the user is currently resizing */
  isResizing: boolean
  /** Clipboard for copy/paste */
  clipboard: SlideElement | null
  /** Clipboard for multi-element copy/paste (e.g. a group of elements) */
  clipboardMulti: SlideElement[] | null
  /** Undo / redo history */
  history: Slide[][]
  historyIndex: number
  /** Currently visible snap guide lines (rendered on canvas during drag/resize) */
  activeSnapLines: { axis: 'x' | 'y'; position: number; start: number; end: number }[]
  /** ID of the container currently being hovered during a drag (for ghost preview) */
  dropTargetContainerId: string | null
  /** The rect of the element being dragged, used to render a ghost inside the drop target */
  dropGhostRect: Rect | null
  /** Index at which the dragged element should be inserted among the container's children */
  dropInsertIndex: number | null
  /** ID of the container the user has "entered" (double-clicked into), Figma-style */
  enteredContainerId: string | null
  /** ID of the group the user has "entered" (double-clicked into), Figma-style */
  enteredGroupId: string | null
  /** Active marquee / lasso selection box (null when not dragging a selection) */
  selectionBox: SelectionBox | null
  /** ID of the image element currently in canvas crop mode, or null */
  croppingElementId: string | null
  /** eventSlug of the presentation currently loaded in the editor. Null when no presentation is loaded. */
  eventSlug: string | null
}

// ─── Resize handles ─────────────────────────────────────────────────────────

export type ResizeHandle =
  | 'top-left'
  | 'top'
  | 'top-right'
  | 'right'
  | 'bottom-right'
  | 'bottom'
  | 'bottom-left'
  | 'left'

// ─── Default factories ─────────────────────────────────────────────────────

export const DEFAULT_TEXT_STYLE: TextStyle = {
  fontFamily: "'Inter', sans-serif",
  fontSize: 24,
  fontWeight: 400,
  fontStyle: 'normal',
  textDecoration: 'none',
  lineHeight: 1.4,
  letterSpacing: 0,
  textTransform: 'none',
  textAlign: 'left',
  verticalAlign: 'top',
  color: '#222222',
}

export const DEFAULT_BORDER: BorderStyle = {
  width: 0,
  color: '#000000',
  radius: 0,
}

export const DEFAULT_PADDING = { top: 0, right: 0, bottom: 0, left: 0 }

export const CANVAS_WIDTH = 1920
export const CANVAS_HEIGHT = 1080

export const DEFAULT_CONSTRAINTS: Constraints = {
  horizontal: 'left',
  vertical: 'top',
}

export const DEFAULT_CONSTRAINT_INSETS: ConstraintInsets = {
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
}

// ─── Font catalogue (shown in the property panel) ───────────────────────────

export const DEFAULT_CONTAINER_PADDING = { top: 0, right: 0, bottom: 0, left: 0 }

export const FONT_OPTIONS = [
  { label: 'Avenir', value: "'Avenir', sans-serif" },
  { label: 'Inter', value: "'Inter', sans-serif" },
  { label: 'IBM Plex Mono', value: "'IBM Plex Mono', monospace" },
  { label: 'Libre Caslon Text', value: "'Libre Caslon Text', serif" },
  { label: 'Libre Baskerville', value: "'Libre Baskerville', serif" },
  { label: 'Arial', value: 'Arial, sans-serif' },
  { label: 'Georgia', value: 'Georgia, serif' },
  { label: 'Courier New', value: "'Courier New', monospace" },
  { label: 'Verdana', value: 'Verdana, sans-serif' },
  { label: 'Times New Roman', value: "'Times New Roman', serif" },
] as const

// ─── Constraint helpers ─────────────────────────────────────────────────────

/**
 * Compute the insets of an element relative to its parent's rect.
 * Used when setting / updating constraints so we can restore the
 * element's position when the parent resizes.
 */
export function computeInsets(
  childRect: Rect,
  parentRect: { width: number; height: number },
): ConstraintInsets {
  return {
    top: childRect.y,
    left: childRect.x,
    right: parentRect.width - (childRect.x + childRect.width),
    bottom: parentRect.height - (childRect.y + childRect.height),
  }
}

/**
 * Apply constraints to compute a new rect for a child element when
 * its parent has been resized from `oldParent` to `newParent`.
 */
export function applyConstraints(
  constraints: Constraints,
  insets: ConstraintInsets,
  childRect: Rect,
  oldParent: { width: number; height: number },
  newParent: { width: number; height: number },
): Rect {
  let x = childRect.x
  let y = childRect.y
  let w = childRect.width
  let h = childRect.height

  // ── Horizontal ──────────────────────────────────────────────────────
  switch (constraints.horizontal) {
    case 'left':
      // Fixed distance from left — x stays the same, width stays the same
      x = insets.left
      break
    case 'right':
      // Fixed distance from right edge
      x = newParent.width - insets.right - w
      break
    case 'left-right':
      // Pinned to both edges — stretches
      x = insets.left
      w = newParent.width - insets.left - insets.right
      break
    case 'center':
      // Centered horizontally, fixed width
      x = (newParent.width - w) / 2
      break
    case 'scale': {
      // Proportional
      const scaleX = newParent.width / oldParent.width
      x = childRect.x * scaleX
      w = childRect.width * scaleX
      break
    }
  }

  // ── Vertical ────────────────────────────────────────────────────────
  switch (constraints.vertical) {
    case 'top':
      y = insets.top
      break
    case 'bottom':
      y = newParent.height - insets.bottom - h
      break
    case 'top-bottom':
      y = insets.top
      h = newParent.height - insets.top - insets.bottom
      break
    case 'center':
      y = (newParent.height - h) / 2
      break
    case 'scale': {
      const scaleY = newParent.height / oldParent.height
      y = childRect.y * scaleY
      h = childRect.height * scaleY
      break
    }
  }

  // Enforce minimum size
  w = Math.max(w, 1)
  h = Math.max(h, 1)

  return {
    x: Math.round(x),
    y: Math.round(y),
    width: Math.round(w),
    height: Math.round(h),
  }
}

/**
 * Compute the bounding box of a set of rects.
 * Useful for calculating a group's bounds from its children.
 */
export function boundingBox(rects: Rect[]): Rect {
  if (rects.length === 0) return { x: 0, y: 0, width: 0, height: 0 }

  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity

  for (const r of rects) {
    minX = Math.min(minX, r.x)
    minY = Math.min(minY, r.y)
    maxX = Math.max(maxX, r.x + r.width)
    maxY = Math.max(maxY, r.y + r.height)
  }

  return {
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY,
  }
}
